export interface SharedFileDto {
    uuid: string;
    file_size: number;
    checksum: string;
    filename: string;
    creation_date: string;
    uploader: string;
    edited_by?: string;
    edited_date?: string;
    directory_path: string;
}

export interface SharedFile {
    uuid: string;
    fileSize: number;
    checksum: string;
    filename: string;
    creationDate: string;
    uploader: string;
    editedBy?: string;
    editedDate?: string;
    directoryPath: string;
}

export function toSharedFile(dto: SharedFileDto): SharedFile {
    return {
        uuid: dto.uuid,
        fileSize: dto.file_size,
        checksum: dto.checksum,
        filename: dto.filename,
        creationDate: dto.creation_date,
        uploader: dto.uploader,
        editedBy: dto.edited_by ?? undefined,
        editedDate: dto.edited_date ?? undefined,
        directoryPath: dto.directory_path,
    };
}

export interface FileNode extends SharedFile {
    isFolder: false;
}

// FolderNode is a frontend-only construct, not a backend DTO. It extends SharedFile solely
// because react-table's Column<FileTreeNode> requires both union variants to share the same
// structural base when using string accessors. The uuid, checksum, creationDate, and uploader
// fields carry no meaning on a folder and are set to empty strings by buildFileTree.
export interface FolderNode extends SharedFile {
    isFolder: true;
    folderPath: string;
    children: FileTreeNode[];
}

export type FileTreeNode = FileNode | FolderNode;

export interface ListSharedFilesResponseDto {
    files: SharedFileDto[];
    cursor?: string;
}

export interface InitiateUploadRequestDto {
    filename: string;
    file_size: number;
    checksum: string;
}

export interface InitiateUploadResponseDto {
    uuid: string;
    presigned_url: string;
}

export interface DownloadFileResponseDto {
    presigned_url: string;
    filename: string;
    file_size: number;
    expiration: number;
}

export interface MoveFileRequestDto {
    uuid: string;
    new_path: string;
    allow_override: boolean;
    new_filename?: string;
}

export function hasHttpStatus(error: unknown): error is Error & { status: number } {
    return (
        error instanceof Error && "status" in error && typeof (error as Error & { status: unknown }).status === "number"
    );
}

export const ROOT_PATH = "/";

export interface FileShareQuotaDto {
    diskQuotaBytes: number;
    downloadLimitBytes: number;
    uploadLimitBytes: number;
}

export interface FileShareConsumptionDto {
    period: string;
    downloadedBytes: number;
    uploadedBytes: number;
    usedDiskBytes: number;
}

export interface FileShareQuotaResponseDto {
    quota?: FileShareQuotaDto;
    consumption: FileShareConsumptionDto;
}

export interface FileShareQuotaResponseRawDto {
    quota?: {
        disk_quota_bytes: number;
        download_limit_bytes: number;
        upload_limit_bytes: number;
    };
    consumption: {
        period: string;
        downloaded_bytes: number;
        uploaded_bytes: number;
        used_disk_bytes: number;
    };
}

export function toFileShareQuotaResponse(dto: FileShareQuotaResponseRawDto): FileShareQuotaResponseDto {
    return {
        quota: dto.quota
            ? {
                  diskQuotaBytes: dto.quota.disk_quota_bytes,
                  downloadLimitBytes: dto.quota.download_limit_bytes,
                  uploadLimitBytes: dto.quota.upload_limit_bytes,
              }
            : undefined,
        consumption: {
            period: dto.consumption.period,
            downloadedBytes: dto.consumption.downloaded_bytes,
            uploadedBytes: dto.consumption.uploaded_bytes,
            usedDiskBytes: dto.consumption.used_disk_bytes,
        },
    };
}
